import React, { useEffect, useState } from "react";
import { Grid, Typography, Divider, CircularProgress } from "@material-ui/core";
import clsx from "clsx";
import moment from "moment-timezone";
import { useStyles } from "./ValetReceiptDetails.styles";
import CashierReceiptService from "../../services/CashierReceiptService";
import GenericError from "../../components/MobilePayment/GenericError";
import { ReceiptContentType } from "../CashierDevice/SummaryReceipt/CashieredDeviceSummaryReceipt";
import apiClient from "../../auth/apiClient";

const cashierReceiptService = new CashierReceiptService(apiClient);

const formatDt = (dt) => {
  return dt ? moment(dt).format("MM/DD/YYYY HH:mm") : "";
};

const ValetReceiptDetails = ({
  parkingActivityId,
  showReceiptHeaderFooter,
  receiptContentType
}) => {
  const classes = useStyles();
  const [receiptDetails, setReceiptDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const PAYMENT_METHODS = {
    CASH: "CASH",
    CREDIT: "CREDIT",
    DEBIT: "DEBIT",
  };

  const getValetReceiptData = async () => {
    try {
      var response = await cashierReceiptService.getReceiptDetails(parkingActivityId);
      const receiptData = response.data;
      if (receiptData != null) {
        setReceiptDetails(receiptData);
      } else {
        setErrorMessage(["Error retrieving Valet Receipt Details."]);
      }
    } catch (error) {
      setErrorMessage(["Error retrieving Valet Receipt Details."]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (parkingActivityId) {
      getValetReceiptData();
    }
  }, [parkingActivityId]);

  const ReceiptRow = ({ label, value }) => (
    <>
      <Grid item xs={6} className={classes.label}>
        {label}
      </Grid>
      <Grid item xs={6} className={classes.value}>
        {value}
      </Grid>
    </>
  );

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      className={clsx(classes.receiptContainer)}
      data-testid="receipt-details"
    >
      {isLoading === true ? (
        <CircularProgress size={60} />
      ) : errorMessage ? (
        <GenericError message={errorMessage} />
      ) : (
        <>
          {receiptDetails && (
            <>
              {showReceiptHeaderFooter && receiptDetails.headerLines?.split("\n").map((line, index) => (
                <Typography key={index} variant="h6" gutterBottom>
                  {line}
                </Typography>
              ))}
              <Typography variant="subtitle1" gutterBottom>
                {showReceiptHeaderFooter && (
                  <> 
                  {receiptContentType === ReceiptContentType.REFUND ? "Refund Receipt" : "Receipt"}
                  </>
                  )}
              </Typography>

              <Grid container spacing={1} className={clsx(classes.container)}>
                <ReceiptRow
                  label="Ticket ID:"
                  value={receiptDetails.ticketID}
                />
                <ReceiptRow
                  label="Transaction ID:"
                  value={receiptDetails.transactionID}
                />
                <ReceiptRow label="Device:" value={receiptDetails.device} />
                <ReceiptRow
                  label="Attendant:"
                  value={
                    receiptDetails.attendantLastName
                      ? `${receiptDetails.attendantFirstName} ${receiptDetails.attendantLastName}`
                      : receiptDetails.attendantFirstName
                  }
                />
                <ReceiptRow
                  label="Entry Time:"
                  value={formatDt(receiptDetails.entryTime)}
                />
                <ReceiptRow
                  label="Paid Time:"
                  value={formatDt(receiptDetails.paidTime)}
                />
                <ReceiptRow
                  label="Parking Time:"
                  value={receiptDetails.parkingTime}
                />
              </Grid>

              <Divider className={clsx(classes.divider)} />

              <Grid container spacing={1} className={clsx(classes.container)}>
                <ReceiptRow
                  label="Parking Fee:"
                  value={`$${receiptDetails.parkingFee.toFixed(2)}`}
                />
                <ReceiptRow
                  label="Taxes:"
                  value={`$${receiptDetails.taxes.toFixed(2)}`}
                />
                <ReceiptRow
                  label="Validations:"
                  value={`$${receiptDetails.validations.toFixed(2)}`}
                />
                <ReceiptRow
                  label="Total Fee:"
                  value={`$${receiptDetails.totalFee.toFixed(2)}`}
                />
              </Grid>

              <Divider className={clsx(classes.divider)} />

              <Typography variant="subtitle1" gutterBottom>
                Payment
              </Typography>

              <Grid container spacing={1} className={clsx(classes.container)}>
                {receiptDetails.paymentMethod.toUpperCase() === PAYMENT_METHODS.CASH && (
                  <>
                    <ReceiptRow
                      label="Payment Method:"
                      value={receiptDetails.paymentMethod}
                    />
                    <ReceiptRow
                      label="Amount Tendered:"
                      value={`$${receiptDetails.cashAmountTendered.toFixed(2)}`}
                    />
                    <ReceiptRow
                      label="Change Due:"
                      value={`$${receiptDetails.cashChangeDue.toFixed(2)}`}
                    />
                  </>
                )}
                {(receiptDetails.paymentMethod.toUpperCase() === PAYMENT_METHODS.CREDIT ||
                  receiptDetails.paymentMethod.toUpperCase() === PAYMENT_METHODS.DEBIT) && (
                    <>
                      <ReceiptRow
                        label="Payment Method:"
                        value={PAYMENT_METHODS.CREDIT}
                      />
                      <Grid item xs={3} className={clsx(classes.label)} />
                      <Grid item xs={6} className={classes.cardType}>
                        {receiptDetails.creditCardType}
                      </Grid>
                      <ReceiptRow
                        label="Account #:"
                        value={receiptDetails.creditCardAccountNumber}
                      />
                      <ReceiptRow
                        label="Credit Card Amount:"
                        value={`$${receiptDetails.creditCardAmount.toFixed(2)}`}
                      />
                    </>
                  )}
              </Grid>
              {showReceiptHeaderFooter && receiptDetails.footerLines?.split("\n").map((line, index) => (
                <Typography key={index} variant="h6" gutterBottom >
                  {line}
                </Typography>
              ))}
            </>
          )}
        </>
      )}
    </Grid>
  );
};

export default ValetReceiptDetails;
